import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useDynamicSEO } from "../hooks/useDynamicSEO";
import { api, formatApiError } from "../lib/api";

const RELIGIONS = ["Hindu", "Muslim", "Christian", "Sikh", "Jain", "Buddhist", "Parsi", "Jewish", "Other"];
const EDUCATIONS = ["Undergraduate", "Graduate", "Postgraduate", "PhD / Doctorate", "Professional"];
const MARITAL = ["Never Married", "Divorced", "Widowed", "Awaiting Divorce"];

const EMPTY_FILTERS = {
  q: "",
  gender: "",
  age_min: "",
  age_max: "",
  religion: "",
  caste: "",
  city: "",
  education: "",
  occupation: "",
  marital_status: "",
};

function cap(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function PublicSearch() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const seo = useDynamicSEO("search");

  const runSearch = useCallback(
    async (opts = {}) => {
      setBusy(true);
      setError("");
      try {
        const params = { ...filters, page, page_size: 12, ...opts };
        const clean = Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined),
        );
        const { data } = await api.get("/search/profiles/public", { params: clean });
        setResults(data.items);
        setTotal(data.total);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setBusy(false);
      }
    },
    [filters, page],
  );

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const applyFilters = () => {
    setPage(1);
    runSearch({ page: 1 });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setTimeout(() => runSearch({ ...EMPTY_FILTERS, page: 1 }), 0);
  };

  const update = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Browse matrimonial profiles — InstaVivah",
    description:
      "Browse public matrimonial profiles by age, religion, caste, city, education and occupation. Register free to view full profiles and connect.",
  };

  return (
    <div>
      <SEO
        title={seo?.title || "Browse Profiles — Free Matrimonial Search"}
        path="/browse"
        canonicalPath={seo?.canonical_path || "/browse"}
        description={
          seo?.description ||
          "Search free matrimonial profiles by age, religion, caste, city, education, and occupation. No login required. Register to view full profiles and connect."
        }
        keywords={
          seo?.keywords ||
          "free matrimonial search, browse profiles, indian matrimonial site, biodata search, marriage profiles online"
        }
        ogTitle={seo?.og_title}
        ogDescription={seo?.og_description}
        ogImage={seo?.og_image}
        ogType={seo?.og_type}
        twitterCard={seo?.twitter_card}
        twitterTitle={seo?.twitter_title}
        twitterDescription={seo?.twitter_description}
        twitterImage={seo?.twitter_image}
        robots={seo?.robots}
        jsonLd={jsonLd}
      />
      <Navbar />

      <header className="sb-page-hero text-center">
        <div className="container">
          <div className="sb-kicker mb-2">Browse profiles — no login required</div>
          <h1>Find your match.</h1>
          <p>
            Search free matrimonial profiles by age, religion, caste, city,
            education and occupation. Register free to view full profiles and
            connect.
          </p>
        </div>
      </header>

      <main className="container py-4" data-testid="public-search-page">
        <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-4">
          <div>
            <h2 className="h4 mb-1">
              {results === null
                ? "Loading…"
                : total > 0
                ? `${total} public profile${total === 1 ? "" : "s"} match your filters`
                : "Try widening your filters"}
            </h2>
            <p className="text-muted mb-0 small">
              We show only summary details on public browse. Register to
              unlock full profiles.
            </p>
          </div>
          <Link to="/register" className="btn btn-gold btn-sm">
            Register Free
          </Link>
        </div>

        <div className="row g-4">
          <aside className="col-lg-3">
            <div className="sb-card" data-testid="public-filters">
              <h3 className="h6 mb-3">Filters</h3>

              <div className="mb-3">
                <label className="form-label">Keyword</label>
                <input
                  className="form-control form-control-sm"
                  value={filters.q}
                  onChange={(e) => update("q", e.target.value)}
                  placeholder="Name, city, occupation…"
                  data-testid="public-filter-q"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Gender</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.gender}
                  onChange={(e) => update("gender", e.target.value)}
                  data-testid="public-filter-gender"
                >
                  <option value="">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Age</label>
                <div className="d-flex gap-2">
                  <input
                    type="number"
                    min="18"
                    max="99"
                    className="form-control form-control-sm"
                    placeholder="Min"
                    value={filters.age_min}
                    onChange={(e) => update("age_min", e.target.value)}
                    data-testid="public-filter-age-min"
                  />
                  <input
                    type="number"
                    min="18"
                    max="99"
                    className="form-control form-control-sm"
                    placeholder="Max"
                    value={filters.age_max}
                    onChange={(e) => update("age_max", e.target.value)}
                    data-testid="public-filter-age-max"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Religion</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.religion}
                  onChange={(e) => update("religion", e.target.value)}
                  data-testid="public-filter-religion"
                >
                  <option value="">Any</option>
                  {RELIGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Caste</label>
                <input
                  className="form-control form-control-sm"
                  value={filters.caste}
                  onChange={(e) => update("caste", e.target.value)}
                  data-testid="public-filter-caste"
                  placeholder="e.g. Brahmin"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Location (city)</label>
                <input
                  className="form-control form-control-sm"
                  value={filters.city}
                  onChange={(e) => update("city", e.target.value)}
                  data-testid="public-filter-city"
                  placeholder="e.g. Mumbai"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Education</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.education}
                  onChange={(e) => update("education", e.target.value)}
                  data-testid="public-filter-education"
                >
                  <option value="">Any</option>
                  {EDUCATIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Occupation</label>
                <input
                  className="form-control form-control-sm"
                  value={filters.occupation}
                  onChange={(e) => update("occupation", e.target.value)}
                  data-testid="public-filter-occupation"
                  placeholder="e.g. Software Engineer"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Marital status</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.marital_status}
                  onChange={(e) => update("marital_status", e.target.value)}
                  data-testid="public-filter-marital"
                >
                  <option value="">Any</option>
                  {MARITAL.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary btn-sm flex-fill"
                  onClick={applyFilters}
                  data-testid="public-filter-apply"
                  disabled={busy}
                >
                  Apply
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={resetFilters}
                  data-testid="public-filter-reset"
                >
                  Reset
                </button>
              </div>
            </div>

            <div
              className="sb-card mt-3"
              style={{
                background: "linear-gradient(135deg, rgba(139,30,65,0.06), rgba(212,175,55,0.10))",
                borderStyle: "dashed",
                borderColor: "var(--sb-gold)",
              }}
            >
              <div className="sb-kicker mb-2">Want more?</div>
              <h4 className="h6">Register to view full profiles &amp; connect.</h4>
              <Link to="/register" className="btn btn-primary btn-sm w-100 mt-2">
                Register Free
              </Link>
            </div>
          </aside>

          <section className="col-lg-9">
            {error && (
              <div className="alert alert-danger" data-testid="public-search-error">
                {error}
              </div>
            )}

            {results === null ? (
              <div className="sb-card">Searching…</div>
            ) : results.length === 0 ? (
              <div className="sb-card text-center py-5" data-testid="public-empty-results">
                <div className="sb-kicker mb-2">No matches</div>
                <h3 className="h5">Try relaxing a filter or two.</h3>
                <p className="text-muted small">
                  Only profiles marked “public” appear here. Members can find
                  many more by <Link to="/register">registering free</Link>.
                </p>
              </div>
            ) : (
              <>
                <div className="row g-3" data-testid="public-search-results">
                  {results.map((r) => (
                    <PublicCard key={r.id} r={r} />
                  ))}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={page <= 1 || busy}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="public-page-prev"
                  >
                    ← Previous
                  </button>
                  <span className="text-muted small">Page {page}</span>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={results.length < 12 || busy}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="public-page-next"
                  >
                    Next →
                  </button>
                </div>
              </>
            )}

            <div
              className="sb-card mt-4 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3"
              style={{
                background: "linear-gradient(135deg, rgba(139,30,65,0.06), rgba(212,175,55,0.10))",
                border: "1px dashed var(--sb-gold)",
              }}
            >
              <div>
                <div className="sb-kicker mb-1">Ready to connect?</div>
                <h3 className="h5 mb-1">Register to view full profiles &amp; connect.</h3>
                <p className="mb-0 text-muted">
                  Unlock photos, contact reveals, interest requests and
                  Premium filters. It’s free to sign up.
                </p>
              </div>
              <Link to="/register" className="btn btn-gold">
                Register free →
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function PublicCard({ r }) {
  const initial = (r.full_name || "?").charAt(0).toUpperCase();
  return (
    <div className="col-md-6 col-xl-4">
      <article className="sb-public-card" data-testid={`public-card-${r.id}`}>
        <div className="d-flex align-items-center gap-3">
          <div className="sb-public-card-avatar" aria-hidden="true">
            {initial}
          </div>
          <div>
            <h3 className="h5 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {r.full_name || "Member"}
            </h3>
            <div className="small text-muted">
              {[r.age && `${r.age} yrs`, cap(r.gender)].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
        </div>

        <dl>
          {r.religion && (<><dt>Religion</dt><dd>{r.religion}</dd></>)}
          {r.caste && (<><dt>Caste</dt><dd>{r.caste}</dd></>)}
          {(r.city || r.state) && (
            <><dt>Location</dt><dd>{[r.city, r.state].filter(Boolean).join(", ")}</dd></>
          )}
          {r.education && (<><dt>Education</dt><dd>{r.education}</dd></>)}
          {r.occupation && (<><dt>Occupation</dt><dd>{r.occupation}</dd></>)}
          {r.marital_status && (<><dt>Marital</dt><dd>{r.marital_status}</dd></>)}
        </dl>

        <div className="sb-private-lock">
          <span>🔒</span>
          <span>Photo, contact &amp; more hidden. Register to view.</span>
        </div>

        <Link
          to="/register"
          className="btn btn-outline-primary btn-sm w-100 mt-3"
          data-testid={`public-register-cta-${r.id}`}
        >
          Register to View Full Profile &amp; Connect
        </Link>
      </article>
    </div>
  );
}
